import * as THREE from 'three';
import { GeometryBuilder } from './geometry-builder';
export type BirdFamily = 'pigeon' | 'gull';

function ellipsoid(
  g: GeometryBuilder,
  x: number,
  y: number,
  z: number,
  rx: number,
  ry: number,
  rz: number,
  color: number,
) {
  const point = (a: number, b: number) =>
    new THREE.Vector3(
      x + rx * Math.sin(a) * Math.cos(b),
      y + ry * Math.cos(a),
      z + rz * Math.sin(a) * Math.sin(b),
    );
  for (let j = 0; j < 5; j++)
    for (let i = 0; i < 10; i++) {
      const a = point((j * Math.PI) / 5, (i * Math.PI) / 5),
        b = point(((j + 1) * Math.PI) / 5, (i * Math.PI) / 5),
        c = point(((j + 1) * Math.PI) / 5, ((i + 1) * Math.PI) / 5),
        d = point((j * Math.PI) / 5, ((i + 1) * Math.PI) / 5);
      const n = d.clone().sub(a).cross(b.clone().sub(a)).normalize();
      if (j === 0) g.triangle(a, c, b, c.clone().sub(a).cross(b.clone().sub(a)).normalize(), color);
      else if (j === 4) g.triangle(a, d, b, n, color);
      else g.quad(a, d, c, b, n, color);
    }
}

export function wildlifeGeometry(kind: 'sheep' | 'cow' | 'bird', family: BirdFamily = 'pigeon') {
  const g = new GeometryBuilder();
  if (kind === 'bird') {
    const gull = family === 'gull',
      white = gull ? 0xe8ebe4 : 0x8d9c9c;
    ellipsoid(g, 0, 0.22, 0.02, gull ? 0.17 : 0.14, 0.17, gull ? 0.37 : 0.26, white);
    ellipsoid(g, 0, 0.34, -0.2, 0.1, 0.17, 0.11, gull ? 0xf0ece1 : 0x688a85);
    ellipsoid(g, 0, 0.45, -0.25, 0.11, 0.11, 0.14, gull ? 0xf0ece1 : 0x829698);
    g.box(-0.028, 0.425, -0.49, 0.056, 0.052, gull ? 0.16 : 0.1, gull ? 0xd9af55 : 0x5b6060);
    if (gull) g.box(-0.028, 0.424, -0.495, 0.056, 0.022, 0.026, 0xb16b54);
    for (const side of [-1, 1]) {
      ellipsoid(g, side * 0.097, 0.472, -0.301, 0.024, 0.025, 0.025, 0x383c36);
      g.box(side * 0.075 - 0.012, 0.025, -0.03, 0.024, 0.12, 0.032, gull ? 0xcaa163 : 0xbb8271);
      for (const toe of [-1, 0, 1])
        g.box(side * 0.075 + toe * 0.019 - 0.009, 0.018, -0.11, 0.017, 0.021, 0.11, 0xb5866a);
    }
    for (let i = 0; i < 5; i++)
      g.box((i - 2) * 0.027 - 0.02, 0.21, 0.2, 0.044, 0.025, gull ? 0.33 : 0.23, gull ? 0xced9d6 : 0x61727b);
    return g.geometry();
  }
  const cow = kind === 'cow',
    body = cow ? 0xe5dfcc : 0xe7e1cf,
    leg = cow ? 0.64 : 0.44;
  ellipsoid(g, 0, leg + 0.35, 0, cow ? 0.45 : 0.36, cow ? 0.46 : 0.32, cow ? 0.93 : 0.66, body);
  if (cow) {
    ellipsoid(g, 0, 1.02, -0.66, 0.33, 0.49, 0.36, 0xd8cbb1);
    ellipsoid(g, 0, 1.0, -1.12, 0.24, 0.3, 0.41, 0xbaa589);
    ellipsoid(g, 0, 0.84, -1.39, 0.255, 0.15, 0.21, 0xc7aaa0);
    for (const side of [-1, 1]) {
      ellipsoid(g, side * 0.423, 1.04, 0.27, 0.035, 0.29, 0.38, 0x62645b);
      ellipsoid(g, side * 0.424, 1.09, -0.4, 0.027, 0.25, 0.21, 0x747168);
      ellipsoid(g, side * 0.29, 1.2, -1.06, 0.22, 0.07, 0.12, 0xb09a7c);
      g.box(side * 0.18 - 0.025, 1.35, -1.07, 0.05, 0.19, 0.06, 0xd5c6a0);
      ellipsoid(g, side * 0.222, 1.14, -1.24, 0.027, 0.032, 0.03, 0x333d36);
    }
  } else {
    for (const [x, y, z] of [
      [-0.22, 0.94, -0.22],
      [0.2, 0.96, 0.1],
      [-0.19, 0.87, 0.35],
      [0.18, 0.9, -0.43],
      [0, 1.02, 0.32],
    ])
      ellipsoid(g, x, y, z, 0.22, 0.18, 0.24, 0xede8d8);
    ellipsoid(g, 0, 0.73, -0.75, 0.175, 0.25, 0.28, 0x766d5c);
    for (const side of [-1, 1]) {
      ellipsoid(g, side * 0.2, 0.87, -0.72, 0.19, 0.055, 0.09, 0xa39174);
      ellipsoid(g, side * 0.158, 0.79, -0.84, 0.025, 0.028, 0.025, 0x313b33);
    }
  }
  for (const x of cow ? [-0.3, 0.3] : [-0.23, 0.23])
    for (const z of cow ? [-0.66, 0.59] : [-0.43, 0.39]) {
      ellipsoid(g, x, leg / 2 + 0.08, z, 0.075, leg / 2 + 0.06, 0.09, cow ? 0xa2977b : 0x8b806a);
      g.box(x - 0.082, 0.015, z - 0.095, 0.164, 0.12, 0.2, 0x4b5249);
      g.box(x - 0.008, 0.015, z - 0.102, 0.016, 0.09, 0.01, 0xb7b39e);
    }
  ellipsoid(g, 0, cow ? 0.72 : 0.69, cow ? 0.93 : 0.67, 0.045, cow ? 0.36 : 0.11, 0.06, body);
  if (cow) ellipsoid(g, 0, 0.4, 0.93, 0.08, 0.12, 0.08, 0x6a695d);
  return g.geometry();
}

/** Separate hinged wings keep both folded and flying silhouettes recognisable. */
export function birdWingGeometry(family: BirdFamily, side: number) {
  const g = new GeometryBuilder(),
    gull = family === 'gull',
    span = gull ? 0.79 : 0.47;
  const a = new THREE.Vector3(side * 0.07, 0.27, -0.14),
    b = new THREE.Vector3(side * span, 0.24, 0.06),
    c = new THREE.Vector3(side * span * 0.78, 0.22, 0.33),
    d = new THREE.Vector3(side * 0.08, 0.25, 0.24),
    n = new THREE.Vector3(0, 1, 0),
    color = gull ? 0xb9c9ca : 0x75878d;
  if (side > 0) g.quad(a, d, c, b, n, color);
  else g.quad(a, b, c, d, n, color);
  if (side > 0) g.quad(a, b, c, d, n.clone().negate(), color);
  else g.quad(a, d, c, b, n.clone().negate(), color);
  for (let i = 0; i < 5; i++)
    g.box(
      side * span * (0.66 + i * 0.055) - 0.035,
      0.23,
      0.13 + i * 0.023,
      0.07,
      0.022,
      0.16,
      gull ? 0x495b60 : 0x526870,
    );
  return g.geometry();
}
